import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
} from "@/components/docs";
import { docsColors } from "@/lib/docs-theme";

export default function PagerDutyIntegrationPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "PagerDuty" },
  ];

  return (
    <DocLayout
      title="PagerDuty Integration - Uptime Monitor Documentation"
      description="Set up PagerDuty integration for advanced incident management, on-call scheduling, and escalation policies."
      canonical="https://uptime-monitor.io/docs/notifications/pagerduty"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="PagerDuty Integration"
        description="Send monitor alerts to PagerDuty using Events API v2. Automatically create and resolve incidents in your PagerDuty service."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>How It Works</h2>
          <p className={`${docsColors.text.body} mb-4`}>
            Uptime Monitor integrates with PagerDuty through the Events API v2 to create incidents
            when monitors go down:
          </p>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Creates incidents when monitors go down
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Automatically resolves incidents when monitors recover (configurable)
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Includes monitor details and error information
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Deduplicates alerts to prevent duplicate incidents
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Links directly to monitor dashboard
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Prerequisites */}
      <DocSection title="Prerequisites">
        <div className="grid md:grid-cols-2 gap-6">
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              PagerDuty Requirements
            </h3>
            <ul className={`space-y-2 ${docsColors.text.body}`}>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Active PagerDuty account (any plan)
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Admin or Manager role for creating integrations
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                At least one PagerDuty service configured
              </li>
            </ul>
          </DocCard>

          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              Uptime Monitor Requirements
            </h3>
            <ul className={`space-y-2 ${docsColors.text.body}`}>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Active Uptime Monitor account
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                At least one monitor configured
              </li>
              <li className="flex items-start">
                <span className={`${docsColors.text.muted} mr-2`}>•</span>
                Admin access to manage integrations
              </li>
            </ul>
          </DocCard>
        </div>
      </DocSection>

      {/* Setup Guide */}
      <DocSection title="Step-by-Step Setup">
        {/* Step 1 */}
        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 1: Create PagerDuty Integration
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Log in to your PagerDuty account and navigate to{" "}
                      <strong className={docsColors.text.heading}>
                        Services → Service Directory
                      </strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Select an existing service or click{" "}
                      <strong className={docsColors.text.heading}>+ New Service</strong> to create
                      one
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      In the service, go to the{" "}
                      <strong className={docsColors.text.heading}>Integrations</strong> tab
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>+ Add Integration</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Search for and select{" "}
                      <strong className={docsColors.text.heading}>Events API v2</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={6} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Name it &quot;Uptime Monitor&quot; and click{" "}
                      <strong className={docsColors.text.heading}>Add</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={7} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Copy the <strong className={docsColors.text.heading}>Integration Key</strong>{" "}
                      (32-character string)
                    </p>
                  </div>
                </div>
              </li>
            </ol>

            <DocAlert variant="info" className="mt-6 p-4">
              <p className={`text-sm ${docsColors.text.body}`}>
                <strong className={docsColors.text.heading}>Important:</strong> Keep this
                integration key secure. It allows sending incidents to your PagerDuty service.
              </p>
            </DocAlert>
          </DocCard>
        </div>

        {/* Step 2 */}
        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 2: Configure in Uptime Monitor
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      In Uptime Monitor, go to{" "}
                      <strong className={docsColors.text.heading}>Settings → Contacts</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>Add Contact</strong> and
                      select <strong className={docsColors.text.heading}>PagerDuty</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Enter a name (e.g., &quot;Production Alerts&quot;) and paste your Integration
                      Key
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Configure auto-resolve setting (enable to automatically resolve incidents when
                      monitors recover)
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>Save the contact</p>
                  </div>
                </div>
              </li>
            </ol>
          </DocCard>
        </div>

        {/* Step 3 */}
        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 3: Assign to Monitors
          </h3>
          <DocCard>
            <p className={`${docsColors.text.body} mb-4`}>
              Now assign the PagerDuty integration to your monitors:
            </p>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Navigate to a monitor you want to alert through PagerDuty
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Edit the monitor and find the{" "}
                      <strong className={docsColors.text.heading}>Contacts</strong> section
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Add your PagerDuty contact to the monitor
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>Save the monitor configuration</p>
                  </div>
                </div>
              </li>
            </ol>

            <DocAlert variant="success" className="mt-6 p-4">
              <p className={`text-sm ${docsColors.text.body}`}>
                <strong className={docsColors.text.heading}>Success!</strong> Your monitor will now
                create PagerDuty incidents when issues are detected.
              </p>
            </DocAlert>
          </DocCard>
        </div>
      </DocSection>

      {/* Event Details */}
      <DocSection title="Incident Information">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Each PagerDuty incident includes comprehensive details:
          </p>

          <DocCodeBlock className="overflow-x-auto">
            <pre className={`text-sm ${docsColors.text.body}`}>
              <code>{`{
  "summary": "Production API is DOWN",
  "source": "uptime-monitor.io",
  "severity": "error",
  "component": "Production API",
  "group": "monitors",
  "class": "HEAD https://api.example.com/",
  "custom_details": {
    "monitor_id": "507f1f77bcf86cd799439011",
    "monitor_name": "Production API",
    "monitor_url": "HEAD https://api.example.com/",
    "monitor_type": "HTTPS",
    "incident_id": "507f1f77bcf86cd799439012",
    "reason": "Connection timeout after 30000ms",
    "check_interval": 60,
    "timeout": 30
  },
  "links": [{
    "href": "https://uptime-monitor.io/monitors/507f1f77bcf86cd799439011",
    "text": "View Monitor Dashboard"
  }]
}`}</code>
            </pre>
          </DocCodeBlock>
        </DocCard>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Create separate PagerDuty contacts for different services
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use different integration keys for production vs staging environments
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Enable auto-resolve for cleaner incident tracking
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Automatically resolve PagerDuty incidents when monitors recover
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use meaningful monitor names</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Monitor names appear in PagerDuty incidents - make them descriptive
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Test integration before production use
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use the Test button in contact settings to verify incidents are created correctly
                  in PagerDuty
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting">
        <div className="space-y-4">
          <DocAlert variant="warning" className="p-6">
            <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
              Common Issues
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                  Incidents not appearing
                </h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Verify the integration key is correct (32-character string)</li>
                  <li>• Ensure monitor has PagerDuty contact assigned</li>
                  <li>• Check monitor is not paused</li>
                </ul>
              </div>

              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                  Integration errors
                </h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Verify you&apos;re using Events API v2 integration</li>
                  <li>• Check integration key hasn&apos;t been regenerated in PagerDuty</li>
                  <li>• Test with a different monitor to isolate issues</li>
                </ul>
              </div>
            </div>
          </DocAlert>
        </div>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications/ms-teams", text: "Microsoft Teams" }}
        next={{ href: "/docs/notifications/incident-io", text: "incident.io" }}
      />
    </DocLayout>
  );
}
